// FilterField.tsx - Reusable field components for filter forms

import { AsyncFormSelect, SelectOption } from "../AsyncSelect";
import type { MultiValue } from "react-select";
import { FilterFieldConfig } from "./FilterModal";
import { json } from "../types";

interface ClearButtonProps {
  onClear: () => void;
}

const ClearButton = ({ onClear }: ClearButtonProps) => (
  <span style={{ marginLeft: 10 }} onClick={onClear}>❌</span>
);

// --- Text Field ---
interface TextFilterFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  transform?: (value: string) => string;
}

export function TextFilterField({ id, label, value, onChange, transform }: TextFilterFieldProps) {
  return (
    <div className="m-3">
      <label className="me-3 fw-bold" htmlFor={id}>{label}</label>
      <input
        type="text"
        className="form-control inline"
        id={id}
        value={value}
        onChange={e => onChange(transform ? transform(e.currentTarget.value) : e.currentTarget.value)}
      />
      {value && <ClearButton onClear={() => onChange("")} />}
    </div>
  );
}

// --- Checkbox Field ---
interface CheckboxFilterFieldProps {
  id: string;
  label: string;
  value: string; // "0" | "1"
  onChange: (value: string) => void;
}

export function CheckboxFilterField({ id, label, value, onChange }: CheckboxFilterFieldProps) {
  return (
    <div className="m-3">
      <label className="me-3 fw-bold" htmlFor={id}>{label}</label>
      <input
        id={id}
        type="checkbox"
        style={{ width: 30, height: 30, verticalAlign: "middle" }}
        checked={value === "1"}
        onChange={e => onChange(e.currentTarget.checked ? "1" : "0")}
      />
      {value === "1" && <ClearButton onClear={() => onChange("0")} />}
    </div>
  );
}

// --- Select Field ---
interface SelectFilterFieldProps {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  defaultValue: string;
  onChange: (value: string) => void;
}

export function SelectFilterField({ id, label, value, options, defaultValue, onChange }: SelectFilterFieldProps) {
  return (
    <div className="m-3">
      <label className="me-3 fw-bold" htmlFor={id}>{label}</label>
      <select className="form-control inline" id={id} value={value} onChange={e => onChange(e.currentTarget.value)}>
        <option value= "">Select</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {value !== defaultValue && <ClearButton onClear={() => onChange(defaultValue)} />}
    </div>
  );
}

// --- Range Field ---
interface RangeFilterFieldProps {
  id: string;
  label: string;
  valueFrom: string;
  valueTo: string;
  onChangeFrom: (value: string) => void;
  onChangeTo: (value: string) => void;
  inputType?: "date" | "number" | "text";
}

export function RangeFilterField({
  id,
  label,
  valueFrom,
  valueTo,
  onChangeFrom,
  onChangeTo,
  inputType = "text",
}: RangeFilterFieldProps) {
  return (
    <div className="m-3">
      <label className="me-3 fw-bold d-block">{label}</label>
      <div className="d-inline-flex align-items-center gap-2">
        <input
          type={inputType}
          className="form-control inline"
          id={`${id}-from`}
          placeholder="From"
          value={valueFrom}
          onChange={e => onChangeFrom(e.currentTarget.value)}
        />
        <span className="mx-1">-</span>
        <input
          type={inputType}
          className="form-control inline"
          id={`${id}-to`}
          placeholder="To"
          value={valueTo}
          onChange={e => onChangeTo(e.currentTarget.value)}
        />
        {(valueFrom || valueTo) && (
          <ClearButton onClear={() => { onChangeFrom(""); onChangeTo(""); }} />
        )}
      </div>
    </div>
  );
}

// --- Async Select Field ---
// Mirrors the pattern used in forms: stores both an ID key and a label key in
// the URL so the selected option can be fully reconstructed on re-open without
// any extra server fetch (no loadInitial needed).
//
// URL params produced (single):  ?organizationId=42&organizationName=Acme+Corp
// URL params produced (multi):   ?tagIds=1,2,3&tagNames=React,TypeScript,Node
interface AsyncSelectFilterFieldProps {
  id: string;
  label: string;
  valueId: string;       // the stored ID(s), comma-separated if isMulti
  valueName: string;     // the stored label(s), comma-separated if isMulti
  onChangeId: (value: string) => void;
  onChangeName: (value: string) => void;
  fileName: string;
  placeholder?: string;
  isMulti?: boolean;
  formValues?: json;
}

export function AsyncSelectFilterField({
  id,
  label,
  valueId,
  valueName,
  onChangeId,
  onChangeName,
  fileName,
  placeholder,
  isMulti = false,
  formValues = {}, 
}: AsyncSelectFilterFieldProps) {
  // Reconstruct the rich SelectOption value from the two flat URL params.
  const selectValue: SelectOption | MultiValue<SelectOption> | null = (() => {
    if (!valueId) return null;
    if (isMulti) {
      const ids = valueId.split(",");
      const labels = valueName.split(",");
      return ids.map((v, i) => ({ value: v, label: labels[i] ?? v }));
    }
    return { value: valueId, label: valueName };
  })();

  const handleChange = (next: SelectOption | MultiValue<SelectOption> | null) => {
    if (!next || (Array.isArray(next) && next.length === 0)) {
      onChangeId("");
      onChangeName("");
      return;
    }
    if (isMulti) {
      const opts = next as MultiValue<SelectOption>;
      onChangeId(opts.map(o => o.value).join(","));
      onChangeName(opts.map(o => o.label).join(","));
    } else {
      const opt = next as SelectOption;
      onChangeId(opt.value);
      onChangeName(opt.label);
    }
  };

  return (
    <div className="m-3 d-flex align-items-center">
      <label className="me-3 fw-bold" htmlFor={id}>{label}</label>
      <div className="flex-grow-1">
        <AsyncFormSelect
          inputId={id}
          fileName={fileName}
          isMulti={isMulti}
          placeholder={placeholder}
          value={selectValue}
          onChange={handleChange}
          formValues = {formValues}
        />
      </div>
      {valueId && (
        <ClearButton onClear={() => { onChangeId(""); onChangeName(""); }} />
      )}
    </div>
  );
}

export function appendField(fd: FormData, params: URLSearchParams, field: FilterFieldConfig) {
    if (field.type === "range") {
        // Two params per range field; neither has a meaningful "default" to skip.
        appendIfPresent(fd, params, `${field.key}From`);
        appendIfPresent(fd, params, `${field.key}To`);
        return;
    }

    if (field.type === "asyncSelect") {
        // Only send the ID to the backend — the label key is UI-only.
        appendIfNotDefault(fd, params, field.key, field.default);
        appendIfNotDefault(fd, params, field.key + "Name", null);
        return;
    }

    // text | checkbox | select
    appendIfNotDefault(fd, params, field.key, field.default);
}

function appendIfPresent(fd: FormData, params: URLSearchParams, key: string) {
    const value = params.get(key);
    if (value !== null && value !== "") fd.append(key, value);
}

function appendIfNotDefault(fd: FormData, params: URLSearchParams, key: string, defaultValue: string | null) {
    const value = params.get(key);
    if (value !== null && value !== defaultValue) fd.append(key, value);
}